import type { TFunction } from 'i18next'
import { PermissionEntityType } from '@/api/model/permissionEntityType'

export interface Permission {
  permissionName: string
  entityType: PermissionEntityType
  targetEntityId: number | null
  isPerEntity?: boolean
}

// An object of an object category (group, action module, …) — only id + display name needed.
export interface SubcategoryItem {
  id: number
  name: string
}

// Permissions of a category: global (General section) vs. per-object.
export interface CategorizedPermission {
  global: Permission[]
  perEntity: Permission[]
}

// A management user as the list returns it (no generated response model available).
export interface CompanyManagementUser {
  id: string
  firstName: string
  lastName: string
  email: string
}

// One category == one entityType.
// Order in the middle column of the company page. Administrators is deliberately absent: a
// company management user must never see or hand out administrator permissions.
export const CATEGORY_ORDER: PermissionEntityType[] = [
  PermissionEntityType.General,
  PermissionEntityType.Permissions,
  PermissionEntityType.Employees,
  PermissionEntityType.CompanyManagementUsers,
  PermissionEntityType.Groups,
  PermissionEntityType.ActionModules,
  PermissionEntityType.ServiceModules,
  PermissionEntityType.ShopModules,
  PermissionEntityType.SubCompanies,
  PermissionEntityType.ServiceConnections,
]

export function getEntityTypeLabels(t: TFunction): Record<PermissionEntityType, string> {
  return {
    [PermissionEntityType.General]: t('entityTypeLabels.General'),
    [PermissionEntityType.Permissions]: t('entityTypeLabels.Permissions'),
    [PermissionEntityType.Employees]: t('entityTypeLabels.Employees'),
    [PermissionEntityType.CompanyManagementUsers]: t('entityTypeLabels.CompanyManagementUsers'),
    [PermissionEntityType.Groups]: t('entityTypeLabels.Groups'),
    [PermissionEntityType.ActionModules]: t('entityTypeLabels.ActionModules'),
    [PermissionEntityType.ServiceModules]: t('entityTypeLabels.ServiceModules'),
    [PermissionEntityType.ShopModules]: t('entityTypeLabels.ShopModules'),
    [PermissionEntityType.SubCompanies]: t('entityTypeLabels.SubCompanies'),
    [PermissionEntityType.ServiceConnections]: t('entityTypeLabels.ServiceConnections'),
    [PermissionEntityType.AdministratorPermissions]: t('entityTypeLabels.AdministratorPermissions'),
    [PermissionEntityType.AdministratorUsers]: t('entityTypeLabels.AdministratorUsers'),
    [PermissionEntityType.AdministratorCompanies]: t('entityTypeLabels.AdministratorCompanies'),
    [PermissionEntityType.AdministratorGeneral]: t('entityTypeLabels.AdministratorGeneral'),
  }
}

// permissionName values are raw strings from the backend (not a generated enum) — fall back to
// the raw value if the backend returns a new value that isn't translated here yet.
export function getPermissionNameLabel(t: TFunction, permissionName: string): string {
  const key = `permissionNameLabels.${permissionName}`
  const translated = t(key)
  return translated === key ? permissionName : translated
}

// Object categories: entityTypes with individual objects (global section + one section per object).
const ENTITY_CATEGORY_TYPES = new Set<PermissionEntityType>([
  PermissionEntityType.AdministratorCompanies,
  PermissionEntityType.Groups,
  PermissionEntityType.ActionModules,
  PermissionEntityType.ServiceModules,
  PermissionEntityType.ShopModules,
  PermissionEntityType.SubCompanies,
])

export function isEntityCategory(entityType: PermissionEntityType): boolean {
  return ENTITY_CATEGORY_TYPES.has(entityType)
}

// Order for the administrator page. Mirrors the company order: the per-page rights first, then
// account management, then what the administrator may do inside a customer portal.
export const ADMINISTRATOR_CATEGORY_ORDER: PermissionEntityType[] = [
  PermissionEntityType.AdministratorGeneral,
  PermissionEntityType.AdministratorPermissions,
  PermissionEntityType.AdministratorUsers,
  PermissionEntityType.AdministratorCompanies,
  ...CATEGORY_ORDER,
]
